import axios from 'axios';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { formatMoment } from '@/shared/converters/dateConverters';
import { OutletId } from '@/shared/models/Outlet';
import { UserId } from '@/shared/models/User';
import { present } from '@/shared/utils/typescript';
import { parseDossierStack, parseDossierStacks } from '../converters/dossierStackConverters';
import { parseDossierStackItems } from '../converters/dossierStackItemConverters';
import DossierStack, { DossierStackData, DossierStackId } from '../models/DossierStack';
import DossierStackItem, { DossierStackItemData, DossierStackItemId } from '../models/DossierStackItem';

export interface DossierStackAssignmentData extends DossierStackItemData {
  stack: DossierStack;
}

export default class DossierStackClient {
  static async loadAll(dossierId: DossierId): Promise<Array<DossierStack>> {
    const response = await axios.get('/api/mobopro/v1/dossierstacks', { params: { dossierid: dossierId } });
    return parseDossierStacks(response.data);
  }

  static async removeDossier(dossierId: DossierId, stackId: DossierStackId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/dossierstacks/${stackId}`);
  }

  static async addDossier(dossierId: DossierId, data: DossierStackAssignmentData): Promise<void> {
    await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/dossierstacks/${data.stack.id}`, {
      memo: data.description || null,
      deadline: formatMoment(data.deadline),
    });
  }

  static async create(data: DossierStackData, userId: UserId): Promise<DossierStack> {
    const response = await axios.post('/api/mobopro/v1/dossierstacks', {
      outletId: data.outlet?.id ?? null,
      outletUserId: userId,
      description: data.description,
      colorHex: data.color,
    });
    return present(parseDossierStack(response.data));
  }

  static async update(id: DossierStackId, data: DossierStackData): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossierstacks/${id}`, {
      description: data.description,
      colorHex: data.color,
    });
  }

  static async remove(id: DossierStackId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossierstacks/${id}`);
  }

  static async loadItems(stackId: DossierStackId,  outletIds: Array<OutletId>): Promise<Array<DossierStackItem>> {
    const response = await axios.get(`/api/mobopro/v1/dossierstacks/${stackId}/content`, { params: { outletid: outletIds } });
    return parseDossierStackItems(response.data);
  }

  static async updateItem(id: DossierStackItemId, stackId: DossierStackId, data: DossierStackItemData): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossierstacks/${stackId}/content/${id}`, {
      memo: data.description,
      deadline: formatMoment(data.deadline),
    });
  }
}